/**
 * <sticky-buy-bar>: de koopbalk onderaan het scherm op mobiel (blocks/sticky-buy-bar.liquid).
 *
 * - Alleen zichtbaar als je voorbij de echte koopknop bent gescrold (Joran, 2026-10-30;
 *   de spec wilde hem vanaf het laden). Zolang de knop in beeld is of nog onder je ligt,
 *   staat hij er niet (data-hidden).
 * - De prijs is die van de koopknop ("In winkelwagen · € 29,95", assets/atc-total.js),
 *   dus hij volgt Kaching's pack en het aantal vanzelf.
 * - De omschrijving is "2 Giftboxes · Reset Mix": de gekozen Kaching-kaart en de naam
 *   van de geurknop van dit product.
 * - Tikken klikt de echte koopknop: Kaching, de cart drawer en Wetracked zien precies
 *   dezelfde toevoeging. Daarbij gaat sticky_atc_click {product, tier, variant} mee.
 *
 * De balk hangt direct onder <body>. Binnen de productkolom (meescrollend, soms met een
 * transform) gedraagt position: fixed zich niet als vast. Rendert de sectie opnieuw,
 * dan vervangt de nieuwe balk de oude.
 */
class StickyBuyBar extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {number | undefined} */
  #frame;

  /** @type {ResizeObserver | undefined} */
  #size;

  /** @type {MutationObserver | undefined} */
  #sync;

  connectedCallback() {
    if (this.parentElement !== document.body) {
      const existing = document.body.querySelector(':scope > sticky-buy-bar');
      if (existing) existing.replaceWith(this);
      else document.body.append(this);
      return; // de verplaatsing roept connectedCallback opnieuw aan
    }

    this.#controller = new AbortController();
    const button = this.#mainButton();
    if (!button) {
      this.hidden = true;
      return;
    }

    this.querySelector('[data-sticky-button]')?.addEventListener('click', this.#onClick, {
      signal: this.#controller.signal,
    });

    // Bij elke scroll (één keer per frame): tonen alleen als de knop boven het scherm
    // verdwenen is. Een IntersectionObserver alleen is niet genoeg: spring je in één
    // keer van onder de knop naar boven, dan raakt de knop het scherm nooit en meldt
    // die niets, en blijft de balk staan.
    const check = () => {
      this.#frame = undefined;
      const scrolledPast = button.getBoundingClientRect().bottom < 0;
      this.toggleAttribute('data-hidden', !scrolledPast);
    };
    const schedule = () => {
      if (this.#frame === undefined) this.#frame = requestAnimationFrame(check);
    };
    window.addEventListener('scroll', schedule, { passive: true, signal: this.#controller.signal });
    window.addEventListener('resize', schedule, { passive: true, signal: this.#controller.signal });
    check();

    this.#size = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--sticky-buy-height', `${this.offsetHeight}px`);
    });
    this.#size.observe(this);

    // Prijs en omschrijving volgen de koopknop en Kaching.
    this.#sync = new MutationObserver(() => requestAnimationFrame(this.#update));
    const total = document.querySelector('.product-details [data-atc-total-value]');
    if (total) this.#sync.observe(total, { childList: true, characterData: true, subtree: true });
    const kaching = document.querySelector('kaching-bundle');
    if (kaching) this.#sync.observe(kaching, { subtree: true, attributes: true, attributeFilter: ['class'] });
    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.#frame !== undefined) cancelAnimationFrame(this.#frame);
    this.#size?.disconnect();
    this.#sync?.disconnect();
  }

  /**
   * De koopknop in het buy-buttons-blok. Niet zomaar de eerste button[name="add"] in de
   * kolom: het intent block heeft per kaart ook een toevoegknop.
   * @returns {HTMLButtonElement | null}
   */
  #mainButton() {
    return document.querySelector('.product-details .buy-buttons-block button[name="add"]');
  }

  #tierTitle() {
    return (
      document
        .querySelector('.kaching-bundles__bar--selected .kaching-bundles__bar-title')
        ?.textContent?.trim() || ''
    );
  }

  #scentName() {
    try {
      const swatches = JSON.parse(document.querySelector('[data-swatch-data]')?.getAttribute('data-swatch-data') || '[]');
      return swatches.find((swatch) => swatch.handle === this.dataset.product)?.name || '';
    } catch (error) {
      return '';
    }
  }

  #update = () => {
    const total = document.querySelector('.product-details [data-atc-total-value]')?.textContent?.trim();
    const price = this.querySelector('[data-sticky-price]');
    if (total && price && price.textContent !== total) price.textContent = total;

    const meta = this.querySelector('[data-sticky-meta]');
    const parts = [this.#tierTitle(), this.#scentName()].filter(Boolean);
    if (meta && parts.length) {
      const text = parts.join(' · ');
      if (meta.textContent !== text) meta.textContent = text;
    }
  };

  #onClick = () => {
    const button = this.#mainButton();
    if (!button || button.disabled) return;
    try {
      const bars = [...document.querySelectorAll('.kaching-bundles__bar')];
      const selected = document.querySelector('.kaching-bundles__bar--selected');
      window.Shopify?.analytics?.publish('sticky_atc_click', {
        product: this.dataset.product,
        tier: selected ? bars.indexOf(selected) + 1 : 1,
        variant: button.form?.querySelector('input[name="id"]')?.value,
      });
    } catch (error) {
      // tracking mag het toevoegen nooit in de weg zitten
    }
    button.click();
  };
}

if (!customElements.get('sticky-buy-bar')) {
  customElements.define('sticky-buy-bar', StickyBuyBar);
}
